import { api } from '../lib/api';

export type Range = [number, number];
export interface Word {
  s: number;
  e: number;
  w: string;
}

/**
 * A piece of another recording, stitched into this one.
 *
 * `at` is a second on THIS recording's original timeline, not a position in
 * the finished video: a clip stays next to the moment it was put beside,
 * whatever gets trimmed around it. `start` and `end` are the in and out
 * points inside the source, which is never touched, so the same recording
 * can be cut differently everywhere it appears.
 */
export interface Clip {
  id: string;
  videoId: string;
  at: number;
  start: number;
  end: number;
}

/**
 * One piece of the finished video, in the order it plays.
 *
 * The edit is this list: pieces of the original recording and clips taken
 * from other recordings, each trimmed to a range inside whatever it comes
 * from, in output order. Order is the list's order, so a piece of the
 * original can play after one recorded later, or after a clip. A self piece
 * carries an `id` only inside the editor, for React and for following one
 * through a drag; it is stripped before the edit is sent.
 */
export type Piece =
  | { kind: 'self'; id?: string; start: number; end: number; title?: string }
  | { kind: 'clip'; id: string; videoId: string; start: number; end: number; title?: string };

/**
 * A chapter of the finished video. A piece with a title begins one, and it
 * runs until the next titled piece or the end. Seconds are on the finished
 * video's clock, the one the player and the share page use.
 */
export interface Chapter {
  title: string;
  startSec: number;
  endSec: number;
}

/** A recording an added clip comes from, as the editor needs to draw it. */
export interface ClipSource {
  videoId: string;
  shareId: string;
  title: string;
  durationSec: number | null;
  posterUrl: string | null;
  url: string | null;
  available: boolean;
  /** Its transcript, on the clock of the version that plays. Absent from an older API. */
  words?: Word[] | null;
}

/** GET /v1/videos/:id/edit */
export interface EditState {
  videoId: string;
  shareId: string;
  title: string;
  kind: 'screen' | 'audio';
  status: string;
  /** The original, as its first render: indexed and on the CDN, so it plays at once. */
  sourceUrl: string;
  /**
   * The raw upload, which streams as fragments and so is what the waveform
   * reader wants. Null or absent once the raw bucket has let it go, or from
   * an older API; the waveform then comes from the render.
   */
  waveformUrl?: string | null;
  sourceDurationSec: number;
  /**
   * The edit, in the order it plays. Absent only from an API build that
   * predates ordered pieces; the editor then derives it from `keep` and
   * `clips`, which every build still sends.
   */
  pieces?: Piece[];
  /** Derived from the titled pieces. Absent from an older API. */
  chapters?: Chapter[];
  keep: Range[] | null;
  clips: Clip[];
  clipSources: ClipSource[];
  version: number;
  /**
   * The cuts and clips above are being rendered into a new version, and
   * what the share page plays is still the previous one.
   */
  rendering: boolean;
  words: Word[] | null;
  transcriptStatus: 'pending' | 'ready' | 'failed' | null;
}

export function fetchEditState(videoId: string): Promise<EditState> {
  return api<EditState>(`/v1/videos/${videoId}/edit`);
}

/** The one recording somebody just picked, with somewhere to scrub it. */
export function fetchClipSource(videoId: string): Promise<ClipSource> {
  return api<ClipSource>(`/v1/videos/${videoId}/clip-source`);
}

/**
 * Save the edit. `pieces` is the edit; `keep` and `clips` are the older
 * shape of the same thing, and are ignored by the API when `pieces` is sent.
 */
export function applyEdit(
  videoId: string,
  body: { pieces?: Piece[]; keep?: Range[] | null; clips?: Clip[]; words?: Word[] },
): Promise<{ ok: boolean; rendering: boolean; version: number }> {
  return api(`/v1/videos/${videoId}/edit`, { method: 'PUT', body });
}
